import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';

import {
  EXPORTS, IMPORTS, SectionHeader, SectionWrapper,
} from 'src/enosikit/components/Chart/components/chartConstants';
import {
  buildTradeTooltipGroups,
  extractTradeValues, getDirectionLabel, getTooltipFormatFunc, getTooltipItemValue,
  isOtherTooltipItemKey,
} from 'src/enosikit/components/Chart/helpers/tooltip';
import {
  BUY, DATA_AGGREGATE_BY_PORTFOLIO, DATA_AGGREGATE_BY_PROPERTY,
  DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE, SELL,
  TRADE_TYPE_ORDER,
} from 'src/util/constants';
import { getTradeTypeLabel } from 'src/util/i18n/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';

/**
 * Resolves the display label for a single trade tooltip line item.
 * @param {import('react-intl').IntlShape} intl i18n intl object.
 * @param {object} datum Tooltip datum for one series.
 * @param {DATA_GROUP_BY_COUNTERPARTY|string} groupBy Active grouping option.
 * @param {boolean} aggByProperty Whether the chart aggregates by property.
 * @returns {string|null} Line item label.
 */
export const formatTradeItemLabel = (intl, datum, groupBy, aggByProperty) => {
  const {
    key, label, property, subLabel, counterPartyUser, counterPartyProperty, tradeType,
  } = datum;

  if (isOtherTooltipItemKey(key)) {
    return label;
  }

  // A counterparty property always identifies the counterparty, so it takes
  // precedence over the source property when grouping by counterparty.
  if (groupBy === DATA_GROUP_BY_COUNTERPARTY && counterPartyProperty) {
    return counterPartyProperty.title || getTradeTypeLabel(intl, tradeType);
  }

  if (aggByProperty && property?.title) {
    return counterPartyUser && subLabel
      ? `${property.title} (${subLabel})`
      : property.title;
  }

  if (groupBy === DATA_GROUP_BY_COUNTERPARTY) {
    if (counterPartyUser) {
      return subLabel || getTradeTypeLabel(intl, tradeType);
    }
    return getTradeTypeLabel(intl, tradeType || label);
  }

  return getTradeTypeLabel(intl, aggByProperty ? subLabel : label);
};

/**
 * Determines whether a tooltip item carries no counterparty or source-property
 * identity of its own, meaning its resolved label would only repeat the group's
 * trade-type total header. Checks for the absence of identifying fields rather
 * than comparing rendered strings, so it is locale-independent and robust to
 * items that omit `label` (e.g. test fixtures) as well as production series where
 * `label` mirrors the trade-type key.
 * @param {object} datum Tooltip datum for one series.
 * @param {boolean} aggByProperty Whether the chart aggregates by property.
 * @returns {boolean} True when the item conveys no identity beyond its trade type.
 */
export const isTradeTypeOnlyItem = (datum, aggByProperty) => {
  const {
    key, property, subLabel, counterPartyUser, counterPartyProperty,
  } = datum;

  if (isOtherTooltipItemKey(key)) {
    // An "Other" residual sub-series carries no source property (`property: null`).
    // Under property aggregation it therefore conveys no property breakdown, so as
    // the sole item in a group — which happens when card pagination hides every real
    // property series for the trade type — it would only repeat the trade-type total.
    // Under portfolio aggregation the "Other" bucket still identifies a distinct
    // rolled-up group, so it is retained.
    return aggByProperty && !property?.title;
  }
  if (counterPartyProperty?.title) {
    return false;
  }
  if (counterPartyUser && subLabel) {
    return false;
  }
  if (aggByProperty && property?.title) {
    return false;
  }
  return true;
};

/**
 * Returns the nested sub-rows to render for a trade-type group. Drops a lone
 * sub-row that would only duplicate the group's trade-type total header, which
 * happens for single-item groups whose item carries no counterparty or
 * source-property identity of its own. Also drops a lone typed "Other" residual
 * whose resolved label is identical to the group's trade-type header: when a
 * trade type is entirely off-page and unselected, its "Other" bucket is labelled
 * with the plain trade-type name rather than the "Other {type}" form, so as the
 * sole item it would repeat the header verbatim (see `formatTradeItemLabel`).
 * @param {object} group Trade-type group from `buildTradeTooltipGroups`.
 * @param {boolean} aggByProperty Whether the chart aggregates by property.
 * @param {string} groupLabel Rendered trade-type header label for the group.
 * @returns {Array<object>} Nested items to render.
 */
export const getGroupNestedItems = (group, aggByProperty, groupLabel) => {
  if (group.items.length !== 1) {
    return group.items;
  }
  const [item] = group.items;
  if (isTradeTypeOnlyItem(item, aggByProperty)) {
    return [];
  }
  if (isOtherTooltipItemKey(item.key) && item.label === groupLabel) {
    return [];
  }
  return group.items;
};

/**
 * Renders a single tooltip line, optionally indented as a nested sub-item.
 * @param {object} props Component props.
 * @param {React.ReactNode} props.label Line label.
 * @param {React.ReactNode} props.value Formatted line value.
 * @param {boolean} props.nested Whether the line is a nested sub-item.
 * @returns {React.ReactElement} Tooltip line.
 */
function TradeTooltipRow({ label = null, value = null, nested = false }) {
  return (
    <Row className="mb-1">
      <Col className={nested ? 'pe-0 ps-4' : 'pe-0'}>
        <span>{label}</span>
      </Col>
      <Col xs="auto">{value}</Col>
    </Row>
  );
}

TradeTooltipRow.propTypes = {
  label: PropTypes.node,
  nested: PropTypes.bool,
  value: PropTypes.node,
};

/**
 * Renders the trade-type groups of one tooltip section. Each group renders a
 * trade-type total, followed by indented sub-rows unless grouping by trade type
 * at the portfolio level, in which case the total already conveys the value and
 * the sub-rows are omitted. A lone sub-row that would only duplicate its
 * trade-type total header is also omitted (see `getGroupNestedItems`).
 * @param {object} props Component props.
 * @param {Array<object>} props.items Tooltip items belonging to the section.
 * @param {object} props.chartView Chart aggregation and grouping options.
 * @param {Function} props.tooltipFormatFunc Value formatter.
 * @param {string} props.unitLabel Unit display label.
 * @returns {React.ReactElement} Section body.
 */
function TradeTooltipGroups({
  items, chartView, tooltipFormatFunc, unitLabel = null,
}) {
  const intl = useIntl();
  const { aggregateBy, groupBy } = chartView;
  const aggByProperty = aggregateBy === DATA_AGGREGATE_BY_PROPERTY;
  // When grouping by trade type at the portfolio level, the group total already
  // conveys the trade-type value, so the nested sub-rows would only repeat it.
  // Suppress them to keep the tooltip to a single line per trade type.
  const showNestedItems = !(groupBy === DATA_GROUP_BY_TRADE_TYPE
    && aggregateBy === DATA_AGGREGATE_BY_PORTFOLIO);
  // `items` is referentially stable across mousemove-only re-renders while the
  // pointer stays over the same bar (see `getTooltipDataFunc` in ChartTrade),
  // so this skips the O(N log N) grouping/sorting work on those re-renders.
  const groups = useMemo(() => buildTradeTooltipGroups(
    items,
    {
      excludeZero: true,
      tradeTypeOrder: TRADE_TYPE_ORDER,
    },
  ), [items]);

  return (
    <>
      {groups.map((group) => {
        const groupLabel = getTradeTypeLabel(intl, group.tradeType);
        const nestedItems = showNestedItems
          ? getGroupNestedItems(group, aggByProperty, groupLabel)
          : [];

        return (
          <React.Fragment key={group.tradeType}>
            <TradeTooltipRow
              label={groupLabel}
              value={tooltipFormatFunc(group.total, unitLabel)}
            />
            {nestedItems.map((datum) => (
              <TradeTooltipRow
                key={datum.key}
                nested
                label={formatTradeItemLabel(intl, datum, groupBy, aggByProperty)}
                value={tooltipFormatFunc(datum.value, unitLabel)}
              />
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
}

TradeTooltipGroups.propTypes = {
  chartView: PropTypes.shape({
    aggregateBy: PropTypes.string,
    groupBy: PropTypes.string,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tooltipFormatFunc: PropTypes.func.isRequired,
  unitLabel: PropTypes.string,
};

/**
 * Renders the tooltip content for a trade chart.
 * @param {object} props Component props.
 * @param {object} props.data Tooltip datapack.
 * @param {string} props.unitLabel Unit display label.
 * @param {object} props.chartView Chart aggregation and grouping options.
 * @param {string} props.unit Chart unit.
 * @returns {React.ReactElement|null} Trade tooltip content.
 */
export default function TradeChartTooltip({
  data = null, unitLabel = null, chartView = null, unit = null,
}) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  // `data` is referentially stable across mousemove-only re-renders while the
  // pointer stays over the same bar (see `getTooltipDataFunc` in ChartTrade),
  // so memoizing per-direction items on it keeps their references stable too,
  // letting `TradeTooltipGroups`'s `useMemo` actually skip recomputing groups
  // on those re-renders. Computed unconditionally (before the `!data` early
  // return below) so the hook call order stays stable across renders.
  const itemsByDirection = useMemo(() => Object.fromEntries(
    [BUY, SELL].map((direction) => [
      direction,
      Object.values(data?.[direction] || {})
        .filter(Boolean)
        .filter((item) => getTooltipItemValue(item.value) !== 0),
    ]),
  ), [data]);

  if (!data || !chartView) {
    return null;
  }

  const values = extractTradeValues(data);
  const tooltipFormatFunc = getTooltipFormatFunc(unit, values, intlCurrency, intl.locale);

  return (
    <>
      {[BUY, SELL].map((direction) => {
        const items = itemsByDirection[direction];
        if (items.length === 0) {
          return null;
        }

        const tooltipLabel = direction === BUY
          ? getDirectionLabel(IMPORTS, intl) : getDirectionLabel(EXPORTS, intl);

        return (
          <SectionWrapper key={direction}>
            <SectionHeader>{tooltipLabel}</SectionHeader>
            <TradeTooltipGroups
              chartView={chartView}
              items={items}
              tooltipFormatFunc={tooltipFormatFunc}
              unitLabel={unitLabel}
            />
          </SectionWrapper>
        );
      })}
    </>
  );
}

TradeChartTooltip.propTypes = {
  chartView: PropTypes.shape({}),
  data: PropTypes.shape({}),
  unit: PropTypes.string,
  unitLabel: PropTypes.string,
};
